import {useAuth} from "../../../../context";
import {useEffect, useState} from "react";
import {config} from "../../../../utils2";
import {fetchWrapper} from "../../../../_services/fetch";
import {useSearchParams} from "react-router-dom";
import * as yup from "yup";

const api = config.get_remote_api() + "/api/medias"

const endpointOrderOptions = (userId) => `${api}/order`
const endpointDetails = (mediaId) => `${api}/${mediaId}`
const endpointCount = (userId, search) => `${api}/count${fetchWrapper.getSearchParams(search)}`
const endpointList = (userId, search) => `${api}/list${fetchWrapper.getSearchParams(search)}`

const endpointAdd = (userId) => `${api}/add`
const endpointEdit = (userId, targetId) => `${api}/${targetId}`
const endpointDelete = (userId, targetId) => `${api}/${targetId}`

const msg = "Failed with no error messages from the server."
const handleSuccess = resp => resp.success ? Promise.resolve(resp?.data) : Promise.reject({message: resp.message || msg})
const handleError = err => err?.name !== "AbortError" ? err?.error || err?.message || msg : null

const defaultValues = {
    id: null,
    description: "",
    path: "",
    type: "",
    file_size_white: 0,
    file_size_black: 0,
    created_at: null,
    updated_at: null,
    user_id: "",
    tags: [],
    products: []
}

export function useMedia(userId, mid = null) {

    const MAX_FILE_SIZE = 30 * 1000 * 1000; // 30MB

    function imageTest(value, required) {
        const fileType = "image"
        const validFileExtensions = {image: ['jpg', 'gif', 'png', 'jpeg', 'svg', "webp"]}

        if (value) {
            const fileName = value.name.toLowerCase()
            return validFileExtensions[fileType].indexOf(fileName.split('.').pop()) > -1;
        }

        // Return true (Valid) if we are in edit or image is not required
        return mid !== "add" || !required;
    }

    const validationSchema = yup.object().shape({
        upload_white: yup.mixed().test("white", "A valid image is required", value => imageTest(value, true)),
        upload_black: yup.mixed().test("black", "A valid image is required", value => imageTest(value, false)),
        // .test("is-valid-size", "Max allowed size is 30 MBytes",
        //     value => value && value.size <= MAX_FILE_SIZE),
        description: yup.string().required("Description is required").max(128),
        tags: yup.array().of(yup.object().shape({
            id: yup.string().uuid(),
            name: yup.string().required(),
            variant: yup.string().required(),
        }))
    })

    const {token} = useAuth()
    const {value, setValue, loadingValue, setLoadingValue, errorValue, setErrorValue} = useMediaDetails(mid, defaultValues)

    function handleSuccessInt(resp) {
        if (resp.success) {
            setValue(resp?.data ? resp.data : defaultValues);
            return Promise.resolve(resp.message);
        } else {
            return Promise.reject({message: resp?.message || "Fetch failed with no error messages from the server."});
        }
    }

    const handleErrorInt = err => {
        setErrorValue(handleError(err))
        return Promise.reject(handleError(err))
    }

    function addItem(formValue) {
        // const {id, ...valuesNoId} = formValue
        const valuesNoId = formValue

        const formData = new FormData()
        if (Object.hasOwn(valuesNoId, "upload_white"))
            formData.append("upload_white", valuesNoId.upload_white)
        if (Object.hasOwn(valuesNoId, "upload_black"))
            formData.append("upload_black", valuesNoId.upload_black)
        if (Object.hasOwn(valuesNoId, "description"))
            formData.append("description", valuesNoId.description)
        if (Object.hasOwn(valuesNoId, "tags"))
            valuesNoId.tags.forEach(tag => formData.append("tag_ids", tag.id))

        // Fetch options
        const requestOptions = {
            method: "POST",
            headers: {...{Authorization: `Bearer ${token.jwt}`}},
            body: formData
        }

        return fetchWrapper.generic(endpointAdd(userId), requestOptions)
            .then(handleSuccessInt)
            .catch(handleErrorInt)
            .finally(() => setLoadingValue(false))
    }

    function editItem(formValue, targetId = mid) {
        const formData = new FormData()
        if (Object.hasOwn(formValue, "upload_white"))
            formData.append("upload_white", formValue.upload_white)
        if (Object.hasOwn(formValue, "upload_black"))
            formData.append("upload_black", formValue.upload_black)
        if (Object.hasOwn(formValue, "description"))
            formData.append("description", formValue.description)
        if (Object.hasOwn(formValue, "tags"))
            formValue.tags.forEach(tag => formData.append("tag_ids", tag.id))

        // Fetch options
        const requestOptions = {
            method: "PUT",
            headers: {...{Authorization: `Bearer ${token.jwt}`}},
            body: formData
        }

        return fetchWrapper.generic(endpointEdit(userId, targetId), requestOptions)
            .then(handleSuccessInt)
            .catch(handleErrorInt)
            .finally(() => setLoadingValue(false))
    }

    function deleteItem(targetId = mid) {
        return fetchWrapper.delete(endpointDelete(userId, targetId), token.jwt)
            .then(handleSuccessInt)
            .catch(handleErrorInt)
            .finally(() => setLoadingValue(false))
    }


    // // function validateItem(edit) {
    // function validateItem() {
    //     let checkOk = true
    //
    //     // if (!edit && !Object.hasOwn(value, "files")) {
    //     if (!value?.id && !Object.hasOwn(value, "files")) {
    //         setInvalidValue(i => ({...i, files: true}))
    //         checkOk = false
    //     }
    //     if (value.description === "") {
    //         setInvalidValue(i => ({...i, description: true}))
    //         checkOk = false
    //     }
    //
    //     // if (value.tags.length === 0) {
    //     //     setInvalidValue(i => ({...i, tags: true}))
    //     //     checkOk = false
    //     // }
    //     return checkOk
    // }

    return {
        value,
        validationSchema,
        addItem,
        editItem,
        deleteItem,
        loadingValue,
        errorValue,
        clearErrorValue: () => setErrorValue(null)
    }
}

export function useMediaDetails(mediaId, defaultValue = defaultValues) {

    const {token} = useAuth()
    const [loadingValue, setLoadingValue] = useState(false);
    const [errorValue, setErrorValue] = useState(null);
    const [value, setValue] = useState(defaultValue);

    useEffect(() => {
        const controller = new AbortController()

        if (mediaId && mediaId !== "add") {
            setErrorValue(null)
            setLoadingValue(true)

            // Fetch options
            const requestOptions = {
                method: "GET",
                headers: {...{Authorization: `Bearer ${token.jwt}`}},
                signal: controller.signal,
            }

            fetchWrapper.generic(endpointDetails(mediaId), requestOptions)
                .then(handleSuccess)
                .then(setValue)
                .catch(err => setErrorValue(handleError(err)))
                .finally(() => setLoadingValue(false))
        }

        return () => {
            controller.abort()
            setLoadingValue(false)
        }
    }, [mediaId]);


    return {
        value,
        setValue,
        loadingValue,
        setLoadingValue,
        errorValue,
        setErrorValue,
        clearError: () => setErrorValue(null)
    }
}

export function useMediaCount(queryOption = null) {

    const {loggedIn, userId, token} = useAuth()
    const [searchParams] = useSearchParams()
    const [loadingCount, setLoadingCount] = useState(false);
    const [errorCount, setErrorCount] = useState(null);
    const [count, setCount] = useState(0);

    // Search options
    const query = queryOption || searchParams.get("query")

    useEffect(() => {
        const controller = new AbortController()

        if (loggedIn) {
            setErrorCount(null)
            setLoadingCount(true)

            // Fetch options
            const requestOptions = {
                method: "GET",
                headers: {...{Authorization: `Bearer ${token.jwt}`}},
                signal: controller.signal,
            }

            // Search params
            const search = {query}

            fetchWrapper.generic(endpointCount(userId, search), requestOptions)
                .then(handleSuccess)
                .then(setCount)
                .catch(err => setErrorCount(handleError(err)))
                .finally(() => setLoadingCount(false))
        }

        return () => {
            controller.abort()
            setLoadingCount(false)
        }
    }, [loggedIn, query]);

    return {
        count,
        setCount,
        loadingCount,
        errorCount,
        clearErrorCount: () => setErrorCount(null)
    }
}


export function useMediaList(pageSizeOption = null, pageIndexOption = null, queryOption = null, orderByOption = null, keepPrev = false) {

    const defaultPageSize = 10
    const defaultPageIndex = 0

    const {loggedIn, userId, token} = useAuth()
    const [searchParams, setSearchParams] = useSearchParams()
    const [loadingList, setLoadingList] = useState(false);
    const [errorList, setErrorList] = useState(null);
    const [list, setList] = useState([]);
    const [reload, setReload] = useState(0);

    // Search options
    const pageSize = pageSizeOption || Number(searchParams.get("size")) || defaultPageSize
    const pageIndex = pageIndexOption || Number(searchParams.get("index")) || defaultPageIndex
    const query = queryOption || searchParams.get("query")
    const orderBy = orderByOption || searchParams.get("by")

    useEffect(() => {
        const controller = new AbortController()

        if (loggedIn && pageIndex != null && pageSize != null) {
            setErrorList(null)
            setLoadingList(true)

            // Fetch options
            const requestOptions = {
                method: "GET",
                headers: {...{Authorization: `Bearer ${token.jwt}`}},
                signal: controller.signal,
            }

            // Search params
            const search = {
                offset: pageIndex * pageSize,
                limit: pageSize,
                query: query,
                order_by: orderBy
            }

            fetchWrapper.generic(endpointList(userId, search), requestOptions)
                .then(handleSuccess)
                .then(data => setList(prev => keepPrev ? ([...prev, ...data]) : data))
                .catch(err => setErrorList(handleError(err)))
                .finally(() => setLoadingList(false))
        }

        return () => {
            controller.abort()
            setLoadingList(false)
        }
    }, [loggedIn, pageIndex, pageSize, query, orderBy, reload]);

    return {
        list,
        setList: fields => {
            setList(prev => {
                const fieldsNew = (typeof fields === "function") ? fields(prev) : fields
                if (fieldsNew.length === 0 && pageIndex > 0) {
                    setSearchParams(prev => {
                        prev.set("index", `${pageIndex - 1}`)
                    })
                } else if (fieldsNew.length < prev.length) {
                    setReload(reload + 1)
                }
                return [...fieldsNew]
            })
        },
        loadingList,
        errorList,
        clearErrorList: () => setErrorList(null)
    }
}

export function useMediaOrderOptions(defaultOptions = []) {

    const {loggedIn, userId, token} = useAuth()
    const [loadingOrderOptions, setLoadingOrderOptions] = useState(false);
    const [errorOrderOptions, setErrorOrderOptions] = useState(null);
    const [orderOptions, setOrderOptions] = useState(defaultOptions);

    useEffect(() => {
        const controller = new AbortController()

        if (loggedIn) {
            setErrorOrderOptions(null)
            setLoadingOrderOptions(true)

            // Fetch options
            const requestOptions = {
                method: "GET",
                headers: {...{Authorization: `Bearer ${token.jwt}`}},
                signal: controller.signal,
            }

            fetchWrapper.generic(endpointOrderOptions(userId), requestOptions)
                .then(handleSuccess)
                .then(setOrderOptions)
                .catch(err => setErrorOrderOptions(handleError(err)))
                .finally(() => setLoadingOrderOptions(false))
        }

        return () => {
            controller.abort()
            setLoadingOrderOptions(false)
        }
    }, [loggedIn]);


    return {
        orderOptions,
        setOrderOptions,
        loadingOrderOptions,
        setLoadingOrderOptions,
        errorOrderOptions,
        setErrorOrderOptions,
        clearErrorOrderOptions: () => setErrorOrderOptions(null)
    }
}